import { useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import {
    CreateAuctionParams,
    UpdateAuctionParams,
    cancelCampaignAuction,
    createCampaignAuction,
    deleteCampaignAuction,
    updateCampaignAuction,
} from 'api/campaigns';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useAuctionsStore } from 'components/pages/campaign-settings/campaign-auction/auction-router/useAuctionsStore';
import { useCurrentPossibleAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { dateFromServer, dateToServerDateTime, getMinDate } from 'helpers/dates';
import { camelCase } from 'lodash';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Auction, InvoiceMethod } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AuctionForm {
    date: [DateTime, DateTime];
    requirePaymentMethod: boolean;
    allowAnonymousBidding: boolean;
    biddingMethod: Auction['bidding_method'];
    terms: string | null;
    invoiceMethod: InvoiceMethod;
    autoCharge: boolean;
    isPublished: boolean;
    allowOnlineBidding: boolean;
    startingBidderNumber: number | null;
}

const useAuctionSettings = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { setShowGettingStarted } = useAuctionsStore();
    const [showDeleteAuction, setShowDeleteAuction] = useState(false);
    const [showCancelAuction, setShowCancelAuction] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    const { isFetching: isFetchingCampaign, data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;
    const { isFetching: isFetchingAuction, data: auction } = useCurrentPossibleAuction();

    const methods = useGivebutterForm<AuctionForm>({
        defaultValues: toForm(auction),
    });
    const { setError, resetFormButton, getValues, watch } = methods;

    const [startingBidderNumber] = watch(['startingBidderNumber']);

    const auctionId = auction?.id!;
    const isBidderNumberDirty = auction?.bidder_number_start !== unmaskNumber(startingBidderNumber);
    const isFetching = isFetchingCampaign || isFetchingAuction;
    const isAuctionOngoing = getIsAuctionOngoing(auction?.start_at, auction?.end_at);
    const [termsRequired, setTermsRequired] = useState(!!auction?.terms);

    const createAuctionMutation = useMutation({
        mutationFn: ({ campaignId, data }: CreateAuctionParams<Partial<Auction>>) =>
            createCampaignAuction({ campaignId, data }),
        onSuccess: () => {
            toast.success('Auction created.');
            propagate();
        },
        onError,
    });

    const updateAuctionMutation = useMutation({
        mutationFn: ({ campaignId, auctionId, data }: UpdateAuctionParams<Partial<Auction>>) =>
            updateCampaignAuction({ campaignId, auctionId, data }),
        onSuccess: () => {
            toast.success('Auction updated.');
            propagate();
        },
        onError,
    });

    const createOrUpdateAuction = async (data: AuctionForm) =>
        auction?.id ? updateAuction(data) : createAuction(data);

    const onSubmit = async (data: AuctionForm) => {
        const shouldShowConfirmation = isAuctionOngoing && isBidderNumberDirty;

        if (shouldShowConfirmation) {
            setShowConfirmationDialog(true);
        } else {
            createOrUpdateAuction(data);
        }
    };

    const onSubmitConfirmation = (confirmed: boolean) => {
        if (confirmed) {
            createOrUpdateAuction(getValues());
        }

        setShowConfirmationDialog(false);
    };

    const createAuction = (data: AuctionForm) =>
        createAuctionMutation.mutateAsync({
            campaignId,
            data: toRequest(data, termsRequired),
        });

    const updateAuction = (data: AuctionForm) =>
        updateAuctionMutation.mutateAsync({
            campaignId,
            auctionId: auction!.id,
            data: toRequest(data, termsRequired),
        });

    const { mutate: onDeleteConfirm } = useMutation({
        mutationFn: () => deleteCampaignAuction({ campaignId, auctionId }),
        onSuccess: () => {
            toast.success('Auction deleted');
            setShowGettingStarted(true);
            propagate();
        },
        onError,
    });

    const { mutate: onCancelConfirm } = useMutation({
        mutationFn: () => cancelCampaignAuction({ campaignId, auctionId }),
        onSuccess: () => {
            toast.success('Auction canceled.');
            propagate();
        },
        onError,
    });

    function onError(error: GivebutterAxiosError) {
        setServerErrors(error);
        resetFormButton();
    }

    const setServerErrors = (error: GivebutterAxiosError) => {
        const data = error.response?.data;
        const errors = data?.errors;

        toast.error(data?.message ?? 'An unknown error occurred');

        if (typeof errors === 'object') {
            for (let key in errors) {
                const typedKey = key as keyof Auction;
                const errorKey =
                    typedKey === 'start_at' || typedKey === 'end_at'
                        ? 'date'
                        : (camelCase(typedKey) as FieldPath<AuctionForm>);

                setError(
                    errorKey,
                    {
                        type: 'custom',
                        message: errors[typedKey][0],
                    },
                    {
                        shouldFocus: true,
                    },
                );
            }
        }
    };

    const onDelete = () => setShowDeleteAuction(true);
    const onCancel = () => setShowCancelAuction(true);
    const propagate = () =>
        queryClient.invalidateQueries({ queryKey: campaignQueryKeys.campaign(campaignId) });

    return {
        campaign,
        auction,
        onSubmit,
        createAuctionMutation,
        updateAuctionMutation,
        termsRequired,
        minDate: getMinDate,
        setTermsRequired,
        showDeleteAuction,
        setShowDeleteAuction,
        onDelete,
        onDeleteConfirm,
        showCancelAuction,
        showConfirmationDialog,
        onSubmitConfirmation,
        setShowCancelAuction,
        onCancel,
        onCancelConfirm,
        methods,
        isFetching,
        isAuctionOngoing,
    };
};

const toForm = (auction: Auction | null): AuctionForm => ({
    date: [
        auction?.start_at
            ? dateFromServer(auction.start_at)
            : DateTime.now().set({ hour: 9, minute: 0 }).plus({ week: 1 }),
        auction?.end_at
            ? dateFromServer(auction.end_at)
            : DateTime.now().set({ hour: 17, minute: 0 }).plus({ week: 1, day: 3 }),
    ],
    requirePaymentMethod: auction?.require_payment_method ?? true,
    allowAnonymousBidding: auction?.allow_anonymous_bidding ?? true,
    biddingMethod: auction?.bidding_method ?? 'manual',
    terms: auction?.terms ?? null,
    invoiceMethod: auction?.invoice_method ?? 'item_close',
    autoCharge: auction?.auto_collect_payment ?? false,
    isPublished: auction?.is_published ?? true,
    allowOnlineBidding: !(auction?.in_person ?? false),
    startingBidderNumber: auction?.bidder_number_start ?? 1,
});

const toRequest = (form: AuctionForm, termsRequired: boolean): Partial<Auction> => ({
    start_at: form.date[0] ? dateToServerDateTime(form.date[0]) : undefined,
    end_at: form.date[1] ? dateToServerDateTime(form.date[1]) : undefined,
    require_payment_method: form.requirePaymentMethod,
    require_shipping: form.requirePaymentMethod,
    allow_anonymous_bidding: form.allowAnonymousBidding,
    terms: termsRequired ? (form.terms ?? '') : null,
    invoice_method: form.invoiceMethod,
    is_published: form.isPublished,
    auto_collect_payment: form.autoCharge,
    bidding_method: form.biddingMethod,
    in_person: !form.allowOnlineBidding,
    bidder_number_start: unmaskNumber(form.startingBidderNumber),
});

const getIsAuctionOngoing = (startAt: string | undefined, endAt: string | undefined) => {
    const currentDateTime = DateTime.now();

    if (!startAt || !endAt) {
        return false;
    }

    const auctionStartDate = dateFromServer(startAt);
    const auctionEndDate = dateFromServer(endAt);

    return auctionStartDate <= currentDateTime && currentDateTime < auctionEndDate;
};

export { useAuctionSettings };
export type { AuctionForm };
